import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Activity, Radar as RadarIcon, RefreshCw, TrendingDown, Zap, CheckSquare, Square,
  AlertTriangle, Clock, MousePointerClick, DollarSign, Filter, Loader2, TrendingUp, Mail,
} from "lucide-react";
import Sparkline from "@/components/Sparkline";

const PRIORITY_STYLES = {
  high:   { border: "border-red-400/50",     text: "text-red-300",     bg: "bg-red-500/5",     dot: "bg-red-400" },
  medium: { border: "border-sky-400/50",   text: "text-sky-300",   bg: "bg-sky-500/5",   dot: "bg-sky-400" },
  low:    { border: "border-emerald-400/40", text: "text-emerald-300", bg: "bg-emerald-500/5", dot: "bg-emerald-400" },
};

function HealthBar({ score }) {
  const color = score >= 80 ? "from-emerald-500 to-emerald-300"
              : score >= 60 ? "from-sky-500 to-sky-300"
              : "from-red-500 to-red-300";
  return (
    <div className="w-full">
      <div className="h-1.5 bg-slate-800 rounded overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${color} transition-all`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

export default function RefreshRadar() {
  const [data, setData] = useState({ summary: {}, items: [] });
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [rowRefreshing, setRowRefreshing] = useState(null);
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  const [filter, setFilter] = useState("due");
  const [priority, setPriority] = useState("");
  const [minAge, setMinAge] = useState(0);
  const [maxClicks, setMaxClicks] = useState("");

  // Trends + digest
  const [trend, setTrend] = useState([]);
  const [digestSending, setDigestSending] = useState(false);
  const [digests, setDigests] = useState([]);

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(""), 3500); };

  const loadTrend = async () => {
    try {
      const [t, d] = await Promise.all([
        api.get("/trends/portfolio?days=30"),
        api.get("/trends/digest/history"),
      ]);
      setTrend(t.data.series || []);
      setDigests(d.data || []);
    } catch {}
  };

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = { filter };
      if (priority) params.priority = priority;
      if (minAge) params.min_age_days = minAge;
      if (maxClicks !== "") params.max_clicks = maxClicks;
      const { data } = await api.get("/refresh-radar", { params });
      setData(data);
      setSelected(new Set());
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter, priority, minAge, maxClicks]); // eslint-disable-line
  useEffect(() => { loadTrend(); }, []);

  const forceSnapshot = async () => {
    try {
      await api.post("/trends/snapshot-now");
      showToast("Snapshot recorded.");
      await loadTrend();
    } catch (e) { showToast("Snapshot failed."); }
  };

  const sendDigest = async () => {
    setDigestSending(true);
    try {
      const { data } = await api.post("/trends/digest/send-mine");
      showToast(data.delivered ? "Digest sent." : `Digest queued (${data.refreshed_count} articles). Add RESEND_API_KEY to email.`);
      await loadTrend();
    } catch (e) {
      showToast("Digest failed.");
    } finally {
      setDigestSending(false);
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === data.items.length) setSelected(new Set());
    else setSelected(new Set(data.items.map((i) => i.id)));
  };

  const bulkRefresh = async () => {
    if (selected.size === 0) { showToast("Select at least one article."); return; }
    if (!window.confirm(`Refresh ${selected.size} article(s) with AI? Each takes ~25s.`)) return;
    setBulkRunning(true);
    try {
      const { data: result } = await api.post("/refresh-radar/bulk-refresh", {
        content_ids: Array.from(selected),
      });
      showToast(`Refreshed ${result.succeeded}/${result.processed}. ${result.failed ? result.failed + " failed." : ""}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBulkRunning(false);
    }
  };

  const refreshRow = async (id) => {
    setRowRefreshing(id);
    try {
      await api.post(`/affiliate/content/${id}/refresh`);
      showToast("Article refreshed.");
      await load();
    } catch (e) {
      showToast("Refresh failed.");
    } finally {
      setRowRefreshing(null);
    }
  };

  const S = data.summary || {};

  return (
    <div className="space-y-8 fade-up">
      {toast && (
        <div data-testid="toast" className="fixed bottom-6 right-6 z-50 bg-sky-400 text-slate-950 px-5 py-3 rounded-md font-medium shadow-2xl">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 border-b border-slate-800 pb-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-2 flex items-center gap-2">
            <RadarIcon className="w-3.5 h-3.5" /> — Refresh Radar
          </p>
          <h1 className="font-serif text-5xl leading-none text-sky-100 tracking-tight">
            Content <span className="italic text-sky-400/90">health check</span>
          </h1>
          <p className="mt-3 text-neutral-400 max-w-2xl">
            See every article's SEO health, get AI refresh suggestions, and bulk-rewrite the ones losing rank in a single click.
          </p>
        </div>
        <button
          data-testid="btn-bulk-refresh"
          onClick={bulkRefresh}
          disabled={selected.size === 0 || bulkRunning}
          className="inline-flex items-center gap-2 bg-sky-400 hover:bg-sky-300 text-slate-950 px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {bulkRunning ? <><Loader2 className="w-4 h-4 animate-spin" /> Refreshing {selected.size}…</> : <><Zap className="w-4 h-4" /> Bulk refresh ({selected.size})</>}
        </button>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="border border-slate-800 rounded-lg p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Total articles</p>
          <p className="font-serif text-3xl text-sky-100 mt-1 font-numeric">{S.total_articles ?? 0}</p>
        </div>
        <div className="border border-sky-500/40 rounded-lg p-4 bg-sky-500/5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400">Showing</p>
          <p className="font-serif text-3xl text-sky-100 mt-1 font-numeric">{S.showing ?? 0}</p>
        </div>
        <div className="border border-slate-800 rounded-lg p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Due now</p>
          <p className="font-serif text-3xl text-sky-100 mt-1 font-numeric">{S.due_now ?? 0}</p>
        </div>
        <div className="border border-red-500/30 rounded-lg p-4 bg-red-500/5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-red-400">High priority</p>
          <p className="font-serif text-3xl text-red-200 mt-1 font-numeric">{S.high_priority ?? 0}</p>
        </div>
        <div className="border border-slate-800 rounded-lg p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Avg health</p>
          <p className="font-serif text-3xl text-sky-100 mt-1 font-numeric">{S.avg_health ?? 0}<span className="text-sm text-neutral-500 font-sans">/100</span></p>
        </div>
      </div>

      {/* Portfolio Health Trend chart + Digest actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-slate-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-sky-400" />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Fig. 04</p>
                <h3 className="font-serif text-xl text-sky-100 mt-0.5">Portfolio health · 30-day trend</h3>
              </div>
            </div>
            <button
              data-testid="btn-snapshot-now"
              onClick={forceSnapshot}
              className="text-xs px-3 py-1.5 border border-slate-800 text-neutral-400 hover:border-sky-400/50 hover:text-sky-200 rounded flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" /> Snapshot now
            </button>
          </div>
          {trend.length === 0 ? (
            <p className="text-sm text-neutral-500 py-12 text-center">No snapshots yet — click "Snapshot now" to record your first data point.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#333" />
                  <XAxis dataKey="date" stroke="#666" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                  <YAxis stroke="#666" fontSize={11} domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#1a1a1a", borderColor: "#333", fontSize: 12, fontFamily: "JetBrains Mono" }}
                    labelStyle={{ color: "#e6b800" }}
                  />
                  <Line dataKey="avg_health" stroke="#e6b800" strokeWidth={2.5} dot={{ r: 4, fill: "#e6b800" }} name="Avg health" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="border border-slate-800 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-5 h-5 text-sky-400" />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Fig. 05</p>
              <h3 className="font-serif text-xl text-sky-100 mt-0.5">Weekly digest</h3>
            </div>
          </div>
          <p className="text-xs text-neutral-500 leading-relaxed">
            A friendly summary of what you refreshed and how traffic responded. Sent every 7 days.
          </p>
          <button
            data-testid="btn-send-digest"
            onClick={sendDigest}
            disabled={digestSending}
            className="mt-4 w-full py-2.5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {digestSending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Composing…</> : <><Mail className="w-3.5 h-3.5" /> Send digest now</>}
          </button>
          {digests.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-800">
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">History ({digests.length})</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {digests.slice(0, 5).map((d, i) => (
                  <div key={i} className="text-[11px] font-mono flex items-center justify-between text-neutral-400">
                    <span className="truncate">{d.refreshed_count} refreshed · {new Date(d.queued_at).toLocaleDateString()}</span>
                    <span className={
                      d.status === "sent" ? "text-emerald-400"
                      : d.status === "queued_log_only" ? "text-sky-300"
                      : "text-red-400"
                    }>● {d.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="border border-slate-800 rounded-xl p-4 flex flex-wrap items-end gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-sky-400" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Filters</span>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Show</p>
          <div className="flex gap-1">
            {["due", "all", "fresh"].map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-xs rounded font-mono uppercase ${filter === f ? "bg-sky-400 text-slate-950" : "border border-slate-800 text-neutral-400 hover:text-sky-200"}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Priority</p>
          <div className="flex gap-1">
            <button onClick={() => setPriority("")} className={`px-3 py-1.5 text-xs rounded font-mono uppercase ${priority === "" ? "bg-sky-400 text-slate-950" : "border border-slate-800 text-neutral-400"}`}>any</button>
            {["high", "medium", "low"].map((p) => (
              <button
                key={p}
                data-testid={`priority-${p}`}
                onClick={() => setPriority(p)}
                className={`px-3 py-1.5 text-xs rounded font-mono uppercase ${priority === p ? "bg-sky-400 text-slate-950" : "border border-slate-800 text-neutral-400"}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Min age (days)</p>
          <input
            data-testid="input-min-age"
            type="number"
            min="0"
            value={minAge}
            onChange={(e) => setMinAge(Number(e.target.value) || 0)}
            className="w-24 bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm text-sky-50 font-mono focus:border-sky-400 outline-none"
          />
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Max clicks</p>
          <input
            data-testid="input-max-clicks"
            type="number"
            min="0"
            value={maxClicks}
            onChange={(e) => setMaxClicks(e.target.value === "" ? "" : Number(e.target.value))}
            placeholder="—"
            className="w-24 bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm text-sky-50 font-mono focus:border-sky-400 outline-none"
          />
        </div>
        <div className="ml-auto">
          <button
            data-testid="btn-toggle-all"
            onClick={toggleAll}
            disabled={data.items.length === 0}
            className="text-xs px-3 py-1.5 border border-slate-800 text-neutral-400 hover:border-sky-400/50 rounded flex items-center gap-1 disabled:opacity-40"
          >
            {selected.size === data.items.length && data.items.length > 0
              ? <><CheckSquare className="w-3 h-3" /> Deselect all</>
              : <><Square className="w-3 h-3" /> Select all</>}
          </button>
        </div>
      </div>

      {error && <div className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-2.5 text-sm rounded">{error}</div>}

      {/* Article list */}
      {loading ? (
        <div className="text-center py-16 text-neutral-500 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin" /> Scanning your library…
        </div>
      ) : data.items.length === 0 ? (
        <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-xl p-12 text-center">
          <Activity className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <p className="font-serif text-xl text-sky-100">Everything's fresh.</p>
          <p className="text-sm text-neutral-500 mt-2">No articles match these filters. Switch to "all" to see the full library, or write more content in the Affiliate Studio.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((it) => {
            const style = PRIORITY_STYLES[it.priority] || PRIORITY_STYLES.medium;
            const isSelected = selected.has(it.id);
            return (
              <div
                key={it.id}
                data-testid={`radar-row-${it.id}`}
                className={`border rounded-xl p-5 transition ${isSelected ? "border-sky-400 bg-sky-500/5" : `border-slate-800 hover:${style.border}`}`}
              >
                <div className="flex items-start gap-4">
                  <button
                    data-testid={`chk-${it.id}`}
                    onClick={() => toggle(it.id)}
                    className="mt-1 flex-shrink-0"
                  >
                    {isSelected ? <CheckSquare className="w-5 h-5 text-sky-400" /> : <Square className="w-5 h-5 text-slate-600 hover:text-sky-300" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${style.border} ${style.text} ${style.bg}`}>
                        <span className={`inline-block w-1 h-1 rounded-full ${style.dot} mr-1.5`} />
                        {it.priority}
                      </span>
                      <span className="font-mono text-[10px] uppercase text-sky-400">{it.network_key}</span>
                      {it.refresh_history_count > 0 && (
                        <span className="text-[10px] font-mono text-emerald-400 flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" /> ×{it.refresh_history_count}
                        </span>
                      )}
                    </div>
                    <h3 className="font-serif text-lg text-sky-100 truncate">{it.title}</h3>
                    <p className="text-xs text-neutral-500 mt-1">{it.niche}</p>

                    {/* Health bar */}
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex-1"><HealthBar score={it.health_score} /></div>
                      <Sparkline contentId={it.id} width={80} height={24} />
                      <span className="font-numeric text-sm text-sky-200 flex-shrink-0 w-14 text-right">{it.health_score}/100</span>
                    </div>

                    {/* Metrics grid */}
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-[11px] font-mono">
                      <div className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-neutral-500" /><span className="text-sky-100">{it.age_days}d old</span></div>
                      <div className="flex items-center gap-1.5"><MousePointerClick className="w-3 h-3 text-neutral-500" /><span className="text-sky-100">{it.clicks} clicks</span></div>
                      <div className="flex items-center gap-1.5"><TrendingDown className="w-3 h-3 text-neutral-500" /><span className="text-sky-100">{it.conversions} conv</span></div>
                      <div className="flex items-center gap-1.5"><DollarSign className="w-3 h-3 text-neutral-500" /><span className="text-sky-300">${it.commission?.toFixed(2) ?? "0.00"}</span></div>
                      <div className="flex items-center gap-1.5"><Activity className="w-3 h-3 text-neutral-500" /><span className="text-sky-100">SEO {it.seo_score}</span></div>
                    </div>

                    {/* AI suggestion */}
                    <div className={`mt-3 flex items-start gap-2 text-xs p-2.5 rounded border ${style.border} ${style.bg}`}>
                      <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${style.text}`} />
                      <p className="text-neutral-300 leading-snug">
                        <span className={`${style.text} font-mono uppercase tracking-widest text-[10px] mr-2`}>AI suggestion</span>
                        {it.suggestion}
                      </p>
                    </div>

                    {it.last_refresh_notes && (
                      <p className="mt-2 text-[11px] text-neutral-500 italic">Last refresh: {it.last_refresh_notes}</p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      data-testid={`btn-refresh-${it.id}`}
                      onClick={() => refreshRow(it.id)}
                      disabled={rowRefreshing === it.id}
                      className="text-xs px-3 py-1.5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded flex items-center gap-1 disabled:opacity-60"
                    >
                      {rowRefreshing === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
